import React, { useState, useCallback } from 'react';
import { useTheme } from '@emotion/react';
import { Grid, Box, Paper, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { loadXLSForm } from '../services/xlsx';
import ExpressionCodingTools from '../components/ExpressionCodingTools';
import QuestionReviewTools from '../components/QuestionReviewTools';
import FormFields from '../components/FormFields';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const useStyles = () => ({
  messageBox: {
    overflow: 'auto',
    padding: '1rem',
    zIndex: 1,
  },
  messageItem: {
    marginTop: '20px',
  },
  chatContainer: {
    boxShadow: '0px -2px 2px -2px #888',
    paddingLeft: '16px',
    paddingRight: '16px',
    zIndex: 1,
    backgroundColor: 'white',
  },
  chat: {
    zIndex: 0,
  },
  textTitle: {
    fontSize: '30px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  titleAbout: {
    fontSize: '24px',
    fontWeight: '500',
    marginBottom: '0px',
  },
  textSubtitle: {
    fontSize: '15px',
    color: '#00658F',
    marginTop: '0px',
  },
  textSubtitleAbout: {
    fontSize: '12px',
  },
  descriptionText: {
    fontSize: '18px',
  },
  descriptionTextAbout: {
    fontSize: '14px',
  },
  userData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '16px',
    marginLeft: 10,
  },
  userSubData: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: '14px',
    marginLeft: 10,
    color: '#41484D',
  },
  linkAbout: {
    color: '#00658F',
    fontSize: '14px',
  },
  changePassword: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginRight: 10,
    marginTop: 10,
  },
  signOut: {
    padding: '10px',
    borderRadius: '20px',
    color: '#00658F',
    borderColor: '#71787E',
    marginTop: 10,
  },
  markdown: {
    width: 'max-content',
    wordBreak: 'break-word',
  },
});

const SurveySection = ({
  logger,
  desktopMode,
  versionText,
  location,
  anonGroup,
  noTitle,
  noExpressions,
  noReviews,
}) => {
  const theme = useTheme();
  const style = useStyles(theme);

  const [fileName, setFileName] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [reviewQuestionsData, setReviewQuestionsData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setFileName(file.name);

    loadXLSForm(file, (error, data) => {
      if (error) {
        console.error(error);
        // set the error state
        setLoadError(error.message);
        // clear the previously-loaded file's contents
        setFileData(null);
      }
      else {
        setLoadError(null);
        setFileData(data);
      }
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <>
      <Grid container spacing={0} alignItems="center">
        {!noTitle && desktopMode && (
          <div
              style={{
                flexDirection: 'column',
                alignItems: 'start',
                width: '100%',
              }}
          >
            <h2 style={style.textTitle}>Survey tools</h2>
            <p style={style.textSubtitle}>
              <a target="_blank" rel="noreferrer" href="/terms-and-conditions">
                {versionText}
              </a>
            </p>
            <hr style={{color: '#C1C7CE'}}/>
          </div>
        )}
        {noTitle && (
          <div
            dangerouslySetInnerHTML={{
              __html: `<!-- Version info: ${versionText} -->`,
            }}
          />
        )}

        <Grid item xs={12}>
          <Box m={1}>
            <Paper elevation={3} style={{ padding: '1em' }}>
              <Typography variant="h6" gutterBottom>
                First: load your form definition
              </Typography>
              <div {...getRootProps()} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '50px',
                borderWidth: '2px',
                borderRadius: '2px',
                borderColor: '#eeeeee',
                borderStyle: 'dashed',
                backgroundColor: '#fafafa',
                color: '#bdbdbd',
                outline: 'none',
                transition: 'border .24s ease-in-out',
                cursor: 'pointer'
              }}>
                <input {...getInputProps()} />
                <p>Drop your .xlsx form file here to load, or click to select it...</p>
              </div>
              {fileName && (
                <Box mt={2} style={{ display: 'flex', alignItems: 'center' }}>
                  {loadError ? (
                    <>
                      <ErrorOutlineIcon style={{ color: 'red' }} />
                      <Typography variant="body1" style={{ marginLeft: '10px', color: 'red' }}>
                        Error loading XLSForm from {fileName}: {loadError}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlineIcon style={{ color: 'green' }} />
                      <Typography variant="body1" style={{ marginLeft: '10px' }}>
                        {noReviews ? `XLSForm loaded from ${fileName}.` : `XLSForm loaded from ${fileName}. Scroll down to view.`}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </Grid>

        {!noExpressions && <ExpressionCodingTools fileData={fileData} logger={logger} anonGroup={anonGroup} location={location}/>}
        {!noReviews && <QuestionReviewTools logger={logger} anonGroup={anonGroup} setReviewQuestionsData={setReviewQuestionsData} location={location} />}
        {!noReviews && <FormFields fileData={fileData} reviewQuestionsData={reviewQuestionsData} logger={logger} anonGroup={anonGroup} location={location} />}
      </Grid>
    </>
  );
};

export default SurveySection;
